import { useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useInfiniteQuery } from '@tanstack/react-query';

import Page from 'components/layouts/page/Page';
import MoreContentRequester from 'components/MoreContentRequester';
import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/Profile';
import { ReviewStatus, ReviewType } from 'models/Review';
import CoordinateRequest from 'models/reviews/CoordinateRequest';
import { coordinateRequestQueryKeys, indexReviews, ReviewsIndexParams } from 'services/api/reviews';
import colors from 'styles/theme/colors';
import { BodySmall, H1, LabelBold } from 'styles/typography';
import ArrowLeft from 'svg/ArrowLeft';
import EmptyCoordinateRequestsIcon from 'svg/EmptyCoordinateRequestsIcon';

import CoordinateRequestRow from './CoordinateRequestRow';

const PAGE_SIZE = 10;

export default function CoordinateRequestsPage() {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const canSeeRequests = profile.has(PERMISSIONS.reviewCoordinateRequestEdit);

  const query = useInfiniteQuery({
    queryKey: coordinateRequestQueryKeys.index(),
    queryFn: ({ pageParam, signal }) =>
      indexReviews(
        {
          type: ReviewType.COORDINATE_REQUEST,
          filters: { status: ReviewStatus.PENDING } as unknown as ReviewsIndexParams['filters'],
          include: 'imported_patient,provider_group,activities',
          sortBy: 'createdAt desc',
          page: pageParam,
          pageSize: PAGE_SIZE,
        },
        signal
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.meta.totalPages > lastPageParam ? lastPageParam + 1 : undefined,
    enabled: canSeeRequests,
  });

  const reviews = useMemo(
    () => (query.data?.pages.flatMap((page) => page.data) ?? []) as CoordinateRequest[],
    [query.data]
  );
  const totalRecords = query.data?.pages[0]?.meta.totalRecords ?? 0;
  const isLoading = query.isLoading;

  if (!canSeeRequests) return <Navigate to='/coordinate' replace />;

  return (
    <Page>
      <Header>
        <BackButton onClick={() => navigate('/coordinate')} aria-label='Back to Coordinate'>
          <ArrowLeft />
        </BackButton>
        <TitleBlock>
          <Title>Coordination Requests</Title>
          <Subtitle>Review incoming requests from providers to complete missing patient details in Olio.</Subtitle>
        </TitleBlock>
      </Header>
      {reviews.length > 0 && <CountHeading>Requests ({totalRecords})</CountHeading>}
      <Content>
        {!isLoading && reviews.length === 0 && <EmptyState />}
        {reviews.length > 0 && (
          <>
            {reviews.map((review) => (
              <CoordinateRequestRow key={review.id} review={review} />
            ))}
            <MoreContentRequester resource={query} />
          </>
        )}
      </Content>
    </Page>
  );
}

function EmptyState() {
  return (
    <EmptyContainer data-testid='coordination-requests-empty'>
      <EmptyIcon>
        <EmptyCoordinateRequestsIcon width={40} height={40} />
      </EmptyIcon>
      <EmptyTitle>No Coordination Requests</EmptyTitle>
      <EmptyBody>
        You&apos;re all caught up. When a provider submits a new coordination request, it will appear here for review.
      </EmptyBody>
    </EmptyContainer>
  );
}

const Header = styled.div`
  align-items: flex-start;
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.black10};
  display: flex;
  gap: 12px;
  padding: 20px 24px;
`;

const BackButton = styled.button`
  all: unset;
  align-items: center;
  cursor: pointer;
  color: ${colors.black75};
  display: flex;
  height: 24px;

  &:hover {
    color: ${colors.black};
  }
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled(H1)`
  font-size: 20px;
  line-height: 24px;
`;

const Subtitle = styled(BodySmall)`
  color: ${colors.black75};
`;

const Content = styled(Page.Content)`
  background-color: ${colors.black03};
  margin-top: 0;
  padding: 8px 24px 24px;
`;

const CountHeading = styled(LabelBold)`
  background-color: ${colors.black03};
  display: block;
  flex: 0 0 auto;
  padding: 16px 24px;
`;

const EmptyContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 80px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  color: ${colors.primaryBlue};
`;

const EmptyTitle = styled(LabelBold)`
  font-size: 16px;
`;

const EmptyBody = styled(BodySmall)`
  color: ${colors.black75};
  max-width: 520px;
`;
